import { h, Component, Host, State, Element, Prop, Watch } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../../common/colorable-component";
import { ContextMenuItemSchemeDefinition, ContextMenuItemSchemeName } from "./constants/context-menu-item-types";
import { CONTEXT_MENU_ITEM_SCHEME_CONFIG, CONTEXT_MENU_ITEM_SCHEME_MAP } from "./constants/context-menu-item-constants";
import { ContextMenuItemSchemeConfiguration } from "../../../constants/colors";
import pfx from "../../../utils/style-class-prefix-helper";
import { ConditionalWrapper } from "../../../common/conditional-wrapper";
import { ContextMenuSize } from "../constants/context-menu-types";
import { CONTEXT_MENU_SIZE_NORMAL } from "../constants/context-menu-constants";
import { CONTEXT_MENU_ITEM_ACTIVE_ICON_SIZE } from "../../../constants/global-constants";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 */
@Component({
  tag: "eon-ui-context-menu-item",
  styleUrl: "context-menu-item.scss",
  shadow: true
})
export class ContextMenuItem
  implements ColorableComponent<ContextMenuItemSchemeConfiguration<ContextMenuItemSchemeDefinition>> {
  schemeConfig: ContextMenuItemSchemeConfiguration<ContextMenuItemSchemeDefinition> = CONTEXT_MENU_ITEM_SCHEME_CONFIG;
  schemeMap = CONTEXT_MENU_ITEM_SCHEME_MAP;

  menuItem: HTMLElement;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ContextMenuItemSchemeDefinition;

  @Element() hostElement: HTMLEonUiContextMenuItemElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ContextMenuItemSchemeName;

  /**
   * Label for each item
   */
  @Prop({ reflect: true }) label: string;

  /**
   * Reflects the native html value attribute. There is no Default Value.
   */
  @Prop({ reflect: true }) value: string;

  /**
   * Label for each item
   */
  @Prop() activeIconName: string;

  /**
   * Selected Menu Item
   */
  @Prop() selectedItem: boolean;

  /**
   * Focus on Menu Item
   */
  @Prop() focusedItem: boolean;

  /**
   * Assign value to href if Menu Items are to be used as link
   * else on selection Menu Items will work as part of default Context Menu
   */
  @Prop() href: string;

  /**
   * target if Menu Items are used as link
   */
  @Prop() target: string;

  /**
   * Set the size of the component
   */
  @Prop() size: ContextMenuSize = CONTEXT_MENU_SIZE_NORMAL;

  componentWillLoad() {
    this.applyScheme();
    this.applyFocus();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("focusedItem")
  applyFocus() {
    if (this.focusedItem) {
      this.menuItem.focus();
    }
  }

  render() {
    return (
      <Host>
        <ConditionalWrapper
          condition={!!this.href}
          wrapper={(children) => (
            <div
              class={pfx({
                "context-menu-item-element": true,
                ["selected-item"]: !!this.selectedItem,
                ["focused-item"]: !!this.focusedItem,
                [this.size]: this.size
              })}
              ref={(el) => (this.menuItem = el as HTMLElement)}
            >
              <a
                class={pfx({
                  "context-menu-item-link": true
                })}
                aria-label={this.label}
                href={this.href}
                target={this.target}
              >
                {children}
              </a>
            </div>
          )}
          defaultWrapper={(children) => (
            <div
              class={pfx({
                "context-menu-item-element": true,
                ["selected-item"]: !!this.selectedItem,
                ["focused-item"]: !!this.focusedItem,
                [this.size]: this.size
              })}
              ref={(el) => (this.menuItem = el as HTMLElement)}
            >
              {children}
            </div>
          )}
        >
          {
            // eslint-disable-next-line jsx-a11y/label-has-associated-control
            <label class={pfx({ "context-menu-item-label": true })} innerHTML={this.label}></label>
          }
          {this.selectedItem && (
            <eon-ui-icon
              aria-label={this.activeIconName}
              class={pfx({
                "context-menu-item-active-icon": true
              })}
              size={CONTEXT_MENU_ITEM_ACTIVE_ICON_SIZE}
              no-space
              name={this.activeIconName}
            ></eon-ui-icon>
          )}
        </ConditionalWrapper>
      </Host>
    );
  }
}
